import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

import useBlocRef from '../utils/useBlocRef.hook';

// Create the context
const AppContext = createContext();

// Create a provider component
export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const appBlocRef = useBlocRef('appBloc');

  useEffect(() => {
    const appBloc = appBlocRef.current;

    // Subscribe to auth$ to get current user
    const authSub = appBloc.auth$.subscribe((auth) => {
      setCurrentUser(auth);
    });

    // Subscribe to userDetails$ to get workspace info
    const userDetailsSub = appBloc.userDetails$.subscribe((userDetails) => {
      if (userDetails && userDetails.workspace) {
        setCurrentWorkspace(userDetails.workspace);
      }
    });

    return () => {
      authSub.unsubscribe();
      userDetailsSub.unsubscribe();
    };
  }, [appBlocRef]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      currentUser,
      currentWorkspace,
      workspaceId: currentWorkspace?.id || localStorage.getItem('workspaceId'),
    }),
    [currentUser, currentWorkspace]
  );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

// Create a custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
